import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Mail, Save, TestTube, Loader2, CheckCircle, XCircle, ArrowLeft, Clock } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const EmailSettingsPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [checkingExpiry, setCheckingExpiry] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [replyToEmail, setReplyToEmail] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [supportFormUrl, setSupportFormUrl] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/email/settings`, { headers });
      setConfig(res.data);
      setSenderEmail(res.data.sender_email || '');
      setReplyToEmail(res.data.reply_to_email || '');
      setSupportEmail(res.data.support_email || '');
      setSupportFormUrl(res.data.support_form_url || '');
      setIsEnabled(res.data.is_enabled || false);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load email settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { sender_email: senderEmail, reply_to_email: replyToEmail, support_email: supportEmail, support_form_url: supportFormUrl, is_enabled: isEnabled };
      if (newApiKey) payload.api_key = newApiKey;
      await axios.put(`${API}/api/email/settings`, payload, { headers });
      toast({ title: 'Saved', description: 'Email settings updated successfully.' });
      setNewApiKey('');
      fetchSettings();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/api/email/test`, {}, { headers });
      toast({ title: 'Test Email Sent', description: res.data.message });
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Failed to send test email', variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-6 px-4 space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/tools')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Mail className="w-6 h-6 text-teal-600" />
              Email Service
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">Configure Emailit API for transactional emails</p>
          </div>
        </div>

        {/* Connection Settings */}
        <Card data-testid="email-settings-card">
          <CardHeader>
            <CardTitle className="text-lg">Connection Settings</CardTitle>
            <CardDescription>Emailit API configuration for sending emails</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Enable Email Service</Label>
                <p className="text-xs text-gray-400">Toggle email sending on/off</p>
              </div>
              <Switch
                checked={isEnabled}
                onCheckedChange={setIsEnabled}
                data-testid="email-enabled-toggle"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">API Key</Label>
              {config?.api_key_masked && !newApiKey ? (
                <div className="flex items-center gap-2 mt-1">
                  <code className="bg-gray-100 px-3 py-1.5 rounded text-sm flex-1">{config.api_key_masked}</code>
                  <Button variant="outline" size="sm" onClick={() => setNewApiKey(' ')} data-testid="change-api-key-btn">
                    Change
                  </Button>
                </div>
              ) : (
                <Input
                  type="password"
                  value={newApiKey}
                  onChange={(e) => setNewApiKey(e.target.value)}
                  placeholder="Enter Emailit API key"
                  className="mt-1"
                  data-testid="api-key-input"
                />
              )}
            </div>

            <div>
              <Label className="text-sm font-medium">Sender Email</Label>
              <Input
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                placeholder="MARQ <noreply@yourdomain.com>"
                className="mt-1"
                data-testid="sender-email-input"
              />
              <p className="text-xs text-gray-400 mt-1">Format: Display Name &lt;email@domain.com&gt;</p>
            </div>

            <div>
              <Label className="text-sm font-medium">Reply-To Email <span className="text-gray-400 text-xs font-normal">(optional)</span></Label>
              <Input
                value={replyToEmail}
                onChange={(e) => setReplyToEmail(e.target.value)}
                placeholder="support@yourdomain.com"
                className="mt-1"
                data-testid="reply-to-email-input"
              />
              <p className="text-xs text-gray-400 mt-1">
                Where user replies land when they hit &ldquo;Reply&rdquo; in their email client. Leave empty to let replies go to the Sender Email. Useful when sending from <code className="bg-gray-100 px-1 rounded text-[11px]">noreply@</code> but wanting replies to reach a monitored inbox.
              </p>
            </div>

            <div>
              <Label className="text-sm font-medium">Support Email</Label>
              <Input
                value={supportEmail}
                onChange={(e) => setSupportEmail(e.target.value)}
                placeholder="support@yourdomain.com"
                className="mt-1"
                data-testid="support-email-input"
              />
              <p className="text-xs text-gray-400 mt-1">
                Auto-injected into every email template as <code className="bg-gray-100 px-1 rounded text-[11px]">{'{{support_email}}'}</code>. Users click this address in emails to reach you (e.g. password-reset help, account issues).
              </p>
            </div>

            <div>
              <Label className="text-sm font-medium">Support Form URL</Label>
              <Input
                value={supportFormUrl}
                onChange={(e) => setSupportFormUrl(e.target.value)}
                placeholder="https://your-notion-form.notion.site/..."
                className="mt-1"
                data-testid="support-form-url-input"
              />
              <p className="text-xs text-gray-400 mt-1">
                External support ticket form (Notion, Typeform, Google Form, etc.). Auto-linked from the Help page footer and Contact Us page. Leave blank to hide the &ldquo;Submit a ticket&rdquo; CTA everywhere.
              </p>
            </div>

            {/* Explainer for the auto-injected placeholders + footer.
                Kept as a callout below the field so admins understand
                what "support_link" resolves to and how to opt out. */}
            <div className="rounded-md border border-blue-100 bg-blue-50/50 p-3 text-xs text-blue-900 space-y-1" data-testid="email-support-explainer">
              <p className="font-semibold">Auto-injected in every email template</p>
              <p>
                <code className="bg-white/70 px-1 rounded">{'{{support_link}}'}</code> resolves to the platform Help page (<code className="bg-white/70 px-1 rounded">/help</code>) so recipients can browse Docs and Guides before opening a ticket.
              </p>
              <p>
                A subtle <em>&ldquo;Need help? Visit our Help Center&rdquo;</em> line is woven into every email footer &mdash; slotted next to the &ldquo;MARQ - Maritime Question Bank Platform&rdquo; tagline where possible. Skipped if the template already uses one of the support variables, or contains the marker <code className="bg-white/70 px-1 rounded">{'<!-- no-support-footer -->'}</code>.
              </p>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-email-settings-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Settings
              </Button>
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={testing || !config?.is_configured}
                data-testid="test-email-btn"
              >
                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                Send Test Email
              </Button>
            </div>

            <div className="flex items-center gap-2 pt-1">
              {config?.is_configured ? (
                <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50">
                  <CheckCircle className="w-3 h-3 mr-1" /> Configured
                </Badge>
              ) : (
                <Badge variant="outline" className="text-gray-500 border-gray-200">
                  <XCircle className="w-3 h-3 mr-1" /> Not configured
                </Badge>
              )}
              <Badge variant="outline" className={isEnabled ? "text-teal-700 border-teal-200 bg-teal-50" : "text-gray-500 border-gray-200"}>
                {isEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card>
          <CardContent className="py-4 space-y-2">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate('/admin/tools/email/templates')}
              data-testid="manage-templates-btn"
            >
              <Mail className="w-4 h-4 mr-2 text-teal-600" />
              Manage Email Templates
              <span className="ml-auto text-xs text-gray-400">Edit subjects, body, enable/disable</span>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={async () => {
                setCheckingExpiry(true);
                try {
                  const res = await axios.post(`${API}/api/email/check-expiry`, {}, { headers });
                  const d = res.data;
                  toast({
                    title: 'Expiry Check Complete',
                    description: `7-day: ${d['7_days_before']?.notified || 0}, Today: ${d.day_of_expiry?.notified || 0}, Expired: ${d.day_after_expiry?.notified || 0} notified`,
                  });
                } catch (err) {
                  toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to run check', variant: 'destructive' });
                } finally {
                  setCheckingExpiry(false);
                }
              }}
              disabled={checkingExpiry || !config?.is_configured}
              data-testid="check-expiry-btn"
            >
              {checkingExpiry ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2 text-amber-600" />}
              Run Expiry Check
              <span className="ml-auto text-xs text-gray-400">Send expiry notifications (7d, today, expired)</span>
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default EmailSettingsPage;
